import { FormScope, Button, Select, TextField } from '../forms';
import { useDraft } from '../recovery/useDraft';
import { DraftNotice } from '../recovery/DraftNotice';
import { planDraftValid, type PlanDraft } from './draft-shapes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, BlockStack, Card, InlineStack } from '@shopify/polaris';
import type { TransferDetail } from './types';
import type { TransferClient } from './useTransferClient';
import { ResourceChoice } from './ResourceChoice';
import { StockPicker } from './StockPicker';
export function TransferDraft({
  client,
  initial,
  close,
}: {
  client: TransferClient;
  initial?: TransferDetail;
  close?: () => void;
}) {
  const navigate = useNavigate();
  const take = initial?.transfer;
  const draft = useDraft<PlanDraft>(client.draftKey + 'transfer-plan:' + (take?.id || 'new'), String(take?.revision || 0), {
    source: take ? { id: take.from_warehouse_id, label: take.from_warehouse_name } : null,
    target: take ? { id: take.to_warehouse_id, label: take.to_warehouse_name } : null,
    reference: take?.reference || '', reason: take?.reason || '', notes: take?.notes || '', shipDate: take?.expected_ship_date || '', arrivalDate: take?.expected_arrival_date || '', priority: String(take?.priority || 0),
    items: initial?.items.filter(x => x.from_location_id && x.from_location_name).map(x => ({ product_id: x.product_id, sku: x.product_sku, title: x.product_title, quantity: String(x.quantity_requested), from_location_id: x.from_location_id, from_location_name: x.from_location_name!, to_location_id: x.to_location_id, to_location_name: x.to_location_name })) || [],
  }, planDraftValid);
  const [source, setSource] = draft.field('source'), [target, setTarget] = draft.field('target'),
    [reference, setReference] = draft.field('reference'), [reason, setReason] = draft.field('reason'),
    [notes, setNotes] = draft.field('notes'), [shipDate, setShipDate] = draft.field('shipDate'),
    [arrivalDate, setArrivalDate] = draft.field('arrivalDate'), [priority, setPriority] = draft.field('priority'), [items, setItems] = draft.field('items');
  const [binEditor, setBinEditor] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [review, setReview] = useState(false);
  const locked = client.locked || review || draft.locked;
  const validate = () => {
    if (!source || !target || source.id === target.id) return setError('Choose two different warehouses.');
    if (
      !items.length ||
      items.some(
        (x) => !Number.isSafeInteger(Number(x.quantity)) || Number(x.quantity) < 1 || Number(x.quantity) > 1000000000,
      )
    )
      return setError('Add stock lines with whole quantities from 1 to 1,000,000,000.');
    if (shipDate && arrivalDate && arrivalDate < shipDate) return setError('Arrival cannot be before departure.');
    setError('');
    setReview(true);
  };
  const submit = async () => {
    const saved = await client.submit(take ? `/${take.id}/${take.lifecycle_version === 1 ? 'edit' : 'adopt'}` : '/', {
      ...(take ? { revision: take.revision } : {}),
      from_warehouse_id: source!.id,
      to_warehouse_id: target!.id,
      reference,
      notes,
      reason,
      priority: Number(priority),
      expected_ship_date: shipDate,
      expected_arrival_date: arrivalDate,
      items: items.map((x) => ({
        product_id: x.product_id,
        quantity: Number(x.quantity),
        from_location_id: x.from_location_id,
        to_location_id: x.to_location_id,
      })),
    }, draft.reference());
    if (saved) { draft.clear(); if (take) close?.(); else navigate('/transfer-orders/' + saved.id); }
  };
  return (
    <FormScope><BlockStack gap="400">
      <Banner tone="info">
        <BlockStack gap="200">
          <p>1. Choose where the goods are now and where they should go.</p>
          <p>2. Add products from the source warehouse and enter the quantities to move.</p>
          <p>3. Review and save the draft. Stock changes only when you later record shipping and receiving.</p>
        </BlockStack>
      </Banner>
      <DraftNotice draft={draft} locked={client.locked} />
      {error && <Banner tone="critical">{error}</Banner>}
      {take?.lifecycle_version === 0 && (
        <Banner tone="warning">
          Review every source bin and quantity. This replaces the older unshipped draft and keeps its previous details
          in history. It does not move stock.
        </Banner>
      )}
      {initial?.nextCursor && <Banner tone="critical">Load all draft items before editing this transfer.</Banner>}
      <Card>
        <BlockStack gap="300">
          <h2>{take ? 'Review draft changes' : '1. Choose the warehouses'}</h2>
          <p>The source sends the goods. The destination receives them. Choose two different warehouses.</p>
          <ResourceChoice
            client={client}
            label="Source warehouse"
            value={source}
            disabled={locked || items.length > 0}
            onChange={setSource}
          />
          {items.length > 0 && <p>Remove all draft lines before changing the source warehouse.</p>}
          <ResourceChoice
            client={client}
            label="Destination warehouse"
            value={target}
            disabled={locked}
            onChange={(value) => {
              setTarget(value);
              setItems(items.map((x) => ({ ...x, to_location_id: null, to_location_name: null })));
            }}
          />
          <h3>Optional transfer details</h3>
          <TextField
            label="Reference (optional)"
            value={reference}
            onChange={setReference}
            maxLength={120}
            disabled={locked}
            autoComplete="off"
            helpText="Leave blank to generate a unique reference."
          />
          <Select
            label="Priority"
            value={priority}
            onChange={setPriority}
            disabled={locked}
            options={[
              { label: 'Normal', value: '0' },
              { label: 'High', value: '1' },
              { label: 'Urgent', value: '2' },
            ]}
          />
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={setReason}
            maxLength={500}
            disabled={locked}
            autoComplete="off"
          />
          <TextField
            label="Planned departure (optional)"
            type="date"
            value={shipDate}
            onChange={setShipDate}
            disabled={locked}
            autoComplete="off"
          />
          <TextField
            label="Planned arrival (optional)"
            type="date"
            value={arrivalDate}
            onChange={setArrivalDate}
            disabled={locked}
            autoComplete="off"
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={setNotes}
            maxLength={2000}
            multiline
            disabled={locked}
            autoComplete="off"
          />
        </BlockStack>
      </Card>
      {!source && <Card><BlockStack gap="200"><h2>2. Add products and quantities</h2><p>Choose a source warehouse above to see its stock. Each line identifies the product and its current storage bin.</p></BlockStack></Card>}
      {items.map((item, index) => (
        <Card key={`${item.product_id}:${item.from_location_id}`}>
          <BlockStack gap="300">
            <h3>
              {item.sku || item.title} · {item.from_location_name}
            </h3>
            <p>{item.title}</p>
            <TextField
              label={`Planned quantity for ${item.sku || item.title} from ${item.from_location_name}`}
              type="number"
              min={1}
              max={1000000000}
              step={1}
              requiredIndicator value={item.quantity}
              disabled={locked}
              autoComplete="off"
              onChange={(value) => setItems(items.map((x, i) => (i === index ? { ...x, quantity: value } : x)))}
            />
            <p>Planned destination bin: {item.to_location_name || 'Choose when receiving'}.</p>
            {target && (
              <Button disabled={locked} onClick={() => setBinEditor(binEditor === index ? null : index)}>
                Choose planned bin for {item.sku || item.title} from {item.from_location_name}
              </Button>
            )}
            {target && binEditor === index && (
              <ResourceChoice
                key={target.id}
                client={client}
                label="Planned destination bin (optional)"
                warehouse={target.id}
                value={
                  item.to_location_id
                    ? { id: item.to_location_id, label: item.to_location_name || 'Selected bin' }
                    : null
                }
                disabled={locked}
                onChange={(value) =>
                  setItems(
                    items.map((x, i) =>
                      i === index
                        ? { ...x, to_location_id: value?.id || null, to_location_name: value?.label || null }
                        : x,
                    ),
                  )
                }
              />
            )}
            <Button disabled={locked} tone="critical" onClick={() => setItems(items.filter((_, i) => i !== index))}>
              Remove line
            </Button>
          </BlockStack>
        </Card>
      ))}
      {!review && source && (
        <StockPicker
          key={source.id}
          client={client}
          warehouse={source.id}
          items={items}
          disabled={client.locked || draft.locked}
          onAdd={(item) => setItems([...items, item])}
        />
      )}
      <Card>
        <BlockStack gap="300">
          <h2>3. Review and save</h2>
          <p>
            {items.length} lines. {items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)} planned units.
            Saving a draft does not reserve or move stock.
          </p>
          {review ? (
            <>
              <h2>Confirm this plan</h2>
              <p>
                {source?.label} to {target?.label}. Check the lines above before saving.
              </p>
              <InlineStack gap="200">
                <Button validate
                  variant="primary"
                  disabled={client.locked || draft.locked || !!draft.error || !!initial?.nextCursor}
                  loading={client.busy}
                  onClick={submit}
                >
                  {take?.lifecycle_version === 0 ? 'Confirm reviewed replacement' : 'Confirm draft'}
                </Button>
                <Button disabled={client.locked} onClick={() => setReview(false)}>
                  Change draft
                </Button>
              </InlineStack>
            </>
          ) : (
            <Button validate variant="primary" disabled={client.locked || draft.locked || !!draft.error || !!initial?.nextCursor} onClick={validate}>
              Review draft
            </Button>
          )}
          {close && (
            <Button disabled={client.locked} onClick={close}>
              Close editor
            </Button>
          )}
        </BlockStack>
      </Card>
    </BlockStack></FormScope>
  );
}
